import React, { useMemo, useState } from "react";
import { app, translate } from "../app";
import { languages } from "../locale";

type SortOption = "newest" | "oldest" | "az" | "za";

function getCategoryLastUpdated(categoryId: number | undefined): number {
  if (!categoryId) return 0;
  // Collect all sections for this category
  const sections = Object.values(app.data.zendesk.sections || {}).filter(
    (s) => s.category === categoryId
  );
  // Find all articles in these sections and get the latest updated_at timestamp
  let latest = 0;
  for (const section of sections) {
    if (!section?.articles) continue;
    for (const article of Object.values(section.articles)) {
      const ts = article?.updated_at ? Date.parse(article.updated_at) : 0;
      if (ts && ts > latest) latest = ts;
    }
  }
  return latest;
}

function getFirstImage(html?: string): string | null {
  if (!html) return null;
  const match = html.match(/<img\s[^>]*src="([^"]+)"/i);
  return match ? match[1] : null;
}

function getTopArticlesForCategory(categoryId: number | undefined, limit = 3) {
  if (!categoryId) return [];
  const sections = Object.values(app.data.zendesk.sections || {}).filter(
    (s) => s.category === categoryId
  );
  const articles: any[] = [];
  for (const section of sections) {
    if (!section?.articles) continue;
    for (const article of Object.values(section.articles)) {
      if (article?.id) {
        articles.push(article);
      }
    }
  }
  // Sort by recency, then keep only those that have a translated title in the current language
  const sorted = articles
    .slice()
    .sort(
      (a, b) =>
        (Date.parse(b?.updated_at || "") || 0) -
        (Date.parse(a?.updated_at || "") || 0)
    );
  const translated = sorted.filter((a) => {
    const titleObj: any = (a as any).name || a.title || {};
    const raw = typeof titleObj === "object" ? titleObj?.[app.locale] : titleObj;
    return !!(raw && String(raw).trim());
  });
  return translated.slice(0, limit).map((a) => {
    const html = translate(a.description || {});
    const image = getFirstImage(html);
    return { ...a, image };
  });
}

export function CategoryGrid() {
  const isRTL = languages[app.locale]?.rtl;
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortOption>("newest");
  const accentColors = ['#FDF1B1', '#F9D79E', '#ABDFBF', '#C0E1E0', '#B1C3FB', '#CDA9E9'];

  const categories = useMemo(() => {
    const list = Object.values(app.data.zendesk.categories || []).map((c) => {
      const lastUpdated = getCategoryLastUpdated(c.id);
      const topArticles = getTopArticlesForCategory(c.id, 3);
      // Get header image from the most recent article
      const headerImage = topArticles.length > 0 && topArticles[0].image ? topArticles[0].image : null;
      return { ...c, lastUpdated, topArticles, headerImage };
    });
    return list;
  }, [app.data.zendesk.categories, app.data.zendesk.sections, app.data.zendesk.articles, app.locale]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let items = categories.filter((c) => {
      if (!q) return true;
      const name = translate(c.name || {}).toLowerCase();
      const desc = translate(c.description || {}).toLowerCase();
      return name.includes(q) || desc.includes(q);
    });

    items = items.sort((a, b) => {
      if (sort === "newest") return (b.lastUpdated || 0) - (a.lastUpdated || 0);
      if (sort === "oldest") return (a.lastUpdated || 0) - (b.lastUpdated || 0);
      if (sort === "az") return translate(a.name || {}).localeCompare(translate(b.name || {}));
      if (sort === "za") return translate(b.name || {}).localeCompare(translate(a.name || {}));
      return 0;
    });
    return items;
  }, [categories, query, sort, app.locale]);

  const formatDate = (ts: number) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleDateString();
    } catch {
      return "";
    }
  };

  return (
    <div className={`category-grid ${isRTL ? "rtl" : ""}`}>
      {/* Toolbar (search and filter) intentionally removed per design */}

      <div className="category-cards">
        {filtered.map((category, index) => {
          const locale = languages[app.locale]?.zendesk?.toLowerCase() ?? app.locale.toLowerCase();
          const href = `/${locale}/categories/${category.id}`;
          const accent = accentColors[index % accentColors.length];
          const headerImage = (category as any).headerImage;
          const isLargeCard = index === 0 && headerImage;
          return (
            <div
              key={category.id}
              className={`category-card ${isLargeCard ? 'category-card-large' : 'category-card-small'}`}
              style={{ ['--accent' as any]: accent }}
            >
              {headerImage && isLargeCard && (
                <a href={href} className="category-card-header-image">
                  <img src={headerImage} alt={translate(category.name || {})} loading="lazy" />
                </a>
              )}
              <div className="category-card-body">
                <div className="category-card-title">
                  <a href={href} className="no-underline">{translate(category.name || {})}</a>
                </div>
                {Array.isArray(category.topArticles) && category.topArticles.length > 0 && (
                  <ul className="category-card-links">
                    {category.topArticles.map((a: any) => (
                      <li key={a.id}>
                        <a href={`/${locale}/articles/${a.id}`}>{translate((a as any).name || a.title || {})}</a>
                      </li>
                    ))}
                  </ul>
                )}
                {category.lastUpdated && (
                  <div className="category-card-meta">
                    {translate({ ["en-US"]: "Last Updated", ["es-ES"]: "Actualizado" })}: {formatDate(category.lastUpdated)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


